import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import generateUUID from "uuid/v4";

import { materialType } from "~/constants/prop-types";
import EventBus from "~/libs/eventBus";
import BookmarkButton from "~/components/Button/BookmarkButton";

import { styled } from "@mui/material/styles";
import PageLayerMaterials from "./PageLayerMaterials";
import MarginalLayerMaterials from "./MarginalLayerMaterials";

const LayerWrapper = styled("div")(props => ({
  pointerEvents: props.isPointerEventsEnabled ? "auto" : "none",
  height: "100%",
  width: "100%",
  position: "absolute",
  top: "0",
  left: "0",
  right: "0",
  bottom: "0"
}));

function MaterialLayerContainer({
  addBookmark,
  removeBookmark,
  showModal,
  moveMaterial,
  updateMaterialPositions,
  bookmarksEnabled,
  page,
  isBookmarked,
  materials,
  marginalLayerHeight,
  activePages,
  isLeftPage,
  isRightPage,
  zoomController
}) {
  const [isPointerEventsEnabled, setIsPointerEventsEnabled] = useState(false);

  useEffect(() => {
    EventBus.addListener("onDragBegin", onDragBegin);
    EventBus.addListener("onDragEnd", onDragEnd);

    return () => {
      EventBus.removeListener("onDragBegin", onDragBegin);
      EventBus.removeListener("onDragEnd", onDragEnd);
    };
  });

  function onDragBegin() {
    zoomController.pause();
    setIsPointerEventsEnabled(true);
  }

  function onDragEnd() {
    zoomController.resume();
    setIsPointerEventsEnabled(false);
  }

  function onPageDrop(item, { difference, absolute }) {
    const scale = zoomController.getScale();

    if (item.position === undefined || item.comingFromSidebar) {
      updateMaterialPositions(item._id, {
        _id: generateUUID(),
        page,
        x: absolute.x / scale,
        y: absolute.y / scale,
        marginal: false
      });
    } else {
      moveMaterial(item._id, item.position._id, {
        page,
        x: item.position.x + difference.x * (1 / scale),
        y: item.position.y + difference.y * (1 / scale),
        marginal: false
      });
    }
  }

  function onMarginalDrop(item, monitor) {
    const position = {
      page,
      marginal: true
    };

    if (item.position === undefined || item.comingFromSidebar) {
      position._id = generateUUID();
      updateMaterialPositions(item._id, position);
    } else {
      moveMaterial(item._id, item.position._id, position);
    }
  }

  function onMaterialClick(material) {
    return event =>
      showModal("SHOW_MATERIAL", {
        id: material._id,
        page: material.position.page,
        materialType: material.materialType,
        renderType: material.renderType
      });
  }

  return (
    <LayerWrapper isPointerEventsEnabled={isPointerEventsEnabled}>
      {bookmarksEnabled && (
        <BookmarkButton
          page={page}
          isBookmarked={isBookmarked}
          addBookmark={addBookmark}
          delBookmark={removeBookmark}
          bookmarksEnabled={bookmarksEnabled}
          isLeftPage={isLeftPage}
          isRightPage={isRightPage}
        />
      )}

      <PageLayerMaterials
        onMaterialDrop={onPageDrop}
        onMaterialClick={onMaterialClick}
        page={page}
        materials={materials}
        marginalLayerHeight={marginalLayerHeight}
        activePages={activePages}
      />

      <MarginalLayerMaterials
        onMaterialDrop={onMarginalDrop}
        onMaterialClick={onMaterialClick}
        page={page}
        materials={materials}
        marginalLayerHeight={marginalLayerHeight}
        activePages={activePages}
      />
    </LayerWrapper>
  );
}

MaterialLayerContainer.propTypes = {
  addBookmark: PropTypes.func.isRequired,
  removeBookmark: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  moveMaterial: PropTypes.func.isRequired,
  bookmarksEnabled: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  isBookmarked: PropTypes.bool.isRequired,
  materials: PropTypes.objectOf(PropTypes.arrayOf(materialType)).isRequired,
  marginalLayerHeight: PropTypes.number.isRequired,
  isLeftPage: PropTypes.bool.isRequired,
  isRightPage: PropTypes.bool.isRequired,
  zoomController: PropTypes.object,
  updateMaterialPositions: PropTypes.func.isRequired,
  activePages: PropTypes.array
};

export default MaterialLayerContainer;
